/**
 * The three-way match between a Bill line, the purchase-order line it bills
 * and the quantity received (piece 24; decisions 0224, 0412, 0413).
 *
 * 🔨 The variance is derived here and stored nowhere: the Bill's own read
 * computes it every time from the line, its order line and the tolerance in
 * force, so moving `bills.varianceTolerancePercent` moves what every
 * unapproved Bill reads as flagged (decision 0412).
 *
 * 🔨 The quantity half applies to an order line that has been received and is
 * skipped on one that has not, because billing before receipt is a supported
 * path and would otherwise flag every time (decision 0413). The price half
 * applies either way.
 */

/** Settings › Accounting › Bill Variance Tolerance: one Organization-scoped row, seeded at 2 percent (decision 0224). */
export const BILL_VARIANCE_TOLERANCE_KEY = 'bills.varianceTolerancePercent';
export const BILL_VARIANCE_TOLERANCE_DEFAULT = 2;

/**
 * 🔨 A tolerance is a percentage from zero up. 💰 Zero flags every rounding
 * difference, which is why the seed is a small percentage and the setting is
 * where it is argued about (decision 0224); zero is accepted, never seeded.
 */
export function billVarianceToleranceProblem(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'the bill variance tolerance is a percentage';
  if (value < 0) return 'the bill variance tolerance is a percentage of zero or more; a negative tolerance flags nothing at all';
  if (value > 100) return 'the bill variance tolerance is a percentage of 100 or less; above that no bill is ever flagged';
  return null;
}

/** One Bill line as the match reads it: what it bills, at what price, and the order line it names. */
export interface BillVarianceLine {
  lineId: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  /** Null on a free-text line, on a line whose Item is on no line of the order, and on every line of a Bill with no purchase order. */
  orderLine: BillVarianceOrderLine | null;
}

/** The order line's own agreed price, and what has been received and billed against it. */
export interface BillVarianceOrderLine {
  unitPriceCents: number;
  received: number;
  /** What approved Bills other than this one already bill against the order line. */
  billedAlready: number;
}

/** Why a line carries no comparison at all, so an absent flag never reads as a pass. */
export type BillVarianceSkip = 'noOrderLine' | 'notYetReceived' | null;

export interface BillLineVariance {
  lineId: string;
  /** How far the billed unit price is above the order's, as a percentage; null where there is nothing to compare. */
  pricePercent: number | null;
  /** How far the billed quantity is above what has been received, as a percentage; null where the quantity is not yet comparable. */
  quantityPercent: number | null;
  priceFlagged: boolean;
  quantityFlagged: boolean;
  flagged: boolean;
  /** Why the quantity half did not run, where it did not. */
  quantitySkip: BillVarianceSkip;
  /** The sentence the Bill's page draws beside the line and the gate carries as a warning; null on an unflagged line. */
  message: string | null;
}

const ROUND2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * 🔨 A price at or below the order's raises nothing: the guard is against
 * paying more than was agreed, and a vendor who invoiced under the order is
 * a credit, not an exception.
 */
function pricePercentAbove(billed: number, agreed: number): number | null {
  if (!Number.isFinite(agreed) || agreed <= 0) return null;
  return ROUND2(((billed - agreed) / agreed) * 100);
}

/** The variance one line carries, against the tolerance in force. */
export function billLineVariance(line: BillVarianceLine, tolerancePercent: number): BillLineVariance {
  const bare = { lineId: line.lineId, pricePercent: null, quantityPercent: null, priceFlagged: false, quantityFlagged: false, flagged: false };
  if (!line.orderLine) return { ...bare, quantitySkip: 'noOrderLine', message: null };
  const pricePercent = pricePercentAbove(line.unitPriceCents, line.orderLine.unitPriceCents);
  const priceFlagged = pricePercent !== null && pricePercent > tolerancePercent;
  // 🔨 Decision 0413: nothing received means nothing to measure against, and the delivery confirmation is the control for that case.
  const received = line.orderLine.received;
  const openReceived = received - line.orderLine.billedAlready;
  const comparable = received > 0;
  const quantityPercent = comparable && openReceived > 0 ? ROUND2(((line.quantity - openReceived) / openReceived) * 100) : comparable ? 100 : null;
  const quantityFlagged = quantityPercent !== null && quantityPercent > tolerancePercent;
  const flagged = priceFlagged || quantityFlagged;
  return {
    lineId: line.lineId,
    pricePercent,
    quantityPercent,
    priceFlagged,
    quantityFlagged,
    flagged,
    quantitySkip: comparable ? null : 'notYetReceived',
    message: flagged ? varianceMessage(line, { pricePercent, priceFlagged, quantityPercent, quantityFlagged }) : null,
  };
}

function varianceMessage(line: BillVarianceLine, facts: { pricePercent: number | null; priceFlagged: boolean; quantityPercent: number | null; quantityFlagged: boolean }): string {
  const parts: string[] = [];
  if (facts.priceFlagged && facts.pricePercent !== null) parts.push(`its unit price is ${facts.pricePercent}% above the order's`);
  if (facts.quantityFlagged && facts.quantityPercent !== null) parts.push(`its quantity is ${facts.quantityPercent}% above what has been received`);
  return `${line.description}: ${parts.join(' and ')}`;
}

/** Every line's variance against one tolerance, in the order the lines arrived. */
export function billVariances(lines: readonly BillVarianceLine[], tolerancePercent: number): BillLineVariance[] {
  return lines.map((line) => billLineVariance(line, tolerancePercent));
}

/**
 * The sentences the gate takes as warnings (decision 0412). A warned document
 * refuses the submitter's rails and takes the `ApprovalRule` chain, and the
 * escalation reason names each variance ahead of the routing sentence.
 */
export function billVarianceWarnings(variances: readonly BillLineVariance[]): string[] {
  return variances.map((row) => row.message).filter((message): message is string => message !== null);
}

/** True where any line is flagged: what the Bill's own page draws as its variance pill. */
export function billIsFlagged(variances: readonly BillLineVariance[]): boolean {
  return variances.some((row) => row.flagged);
}
